import { Table, TableColumn, Typography } from '@scale/scaleui';
import { ISensorFusionStageDefinitionLookupDto } from '@scale/sensor-fusion-shared';
import { SubstageInstanceStatus, SubstageInstanceStatusValues } from '@scale/shared-types';

import {
  CORP_LOOKUP_PATH,
  CORP_SUBSTAGES_PATH,
  REMOADMIN_LOOKUP_PATH,
  REMOADMIN_SUBSTAGES_PATH,
} from '../constants';
import { useSensorFusionLookupContext } from '../contexts/SensorFusionLookupContext';
import { ReactElement, useMemo } from 'react';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

type StageDefinitionBreakdownPanelProps = {
  stageDefinition: ISensorFusionStageDefinitionLookupDto;
};

const TABLE_COLUMNS: TableColumn[] = [
  { header: '', id: 'claimed' },
  { header: 'Not Started', id: SubstageInstanceStatus.NotStarted },
  { header: 'In Progress', id: SubstageInstanceStatus.InProgress },
  { header: 'Completed', id: SubstageInstanceStatus.Completed },
  { header: 'Total', id: 'total' },
];

const StageDefinitionBreakdownPanel = ({ stageDefinition }: StageDefinitionBreakdownPanelProps) => {
  const { isRemoAdmin } = useRemoAdminCtx();
  const { scaleDashboardFrontendUrl, remotasksFrontendUrl } = useSensorFusionLookupContext() ?? {};

  const toolLookupUrl = useMemo(() => {
    if (isRemoAdmin) {
      return `${remotasksFrontendUrl ?? ''}/${REMOADMIN_LOOKUP_PATH}`;
    }
    return `${scaleDashboardFrontendUrl ?? ''}/${CORP_LOOKUP_PATH}`;
  }, [isRemoAdmin, remotasksFrontendUrl, scaleDashboardFrontendUrl]);

  const toolSubstagesUrl = useMemo(() => {
    if (isRemoAdmin) {
      return `${remotasksFrontendUrl ?? ''}/${REMOADMIN_SUBSTAGES_PATH}`;
    }
    return `${scaleDashboardFrontendUrl ?? ''}/${CORP_SUBSTAGES_PATH}`;
  }, [isRemoAdmin, remotasksFrontendUrl, scaleDashboardFrontendUrl]);

  const tablesData = stageDefinition.substageDefinitions.map(({ id, name, statusCounts }) => ({
    id,
    substage: (
      <a className="text-info-700" href={`${toolLookupUrl}/${id}`}>
        {name}
      </a>
    ),
    data: [
      {
        claimed: 'Claimed',
        ...Object.entries(statusCounts.claimed).reduce((acc, [status, count]) => {
          const statusKey = SubstageInstanceStatus[status as keyof typeof SubstageInstanceStatus];

          acc[statusKey] = (
            <a
              key={`${name}/claimed/${status}`}
              className="text-info-700"
              href={`${toolSubstagesUrl}/${stageDefinition.project.id}?substage=${stageDefinition._id}&claimed=true&status=${status}`}
            >
              {count}
            </a>
          );
          return acc;
        }, {} as { [key in SubstageInstanceStatusValues]: ReactElement }),
        total: (
          <a
            key={`${name}/claimed/total`}
            className="text-info-700"
            href={`${toolSubstagesUrl}/${stageDefinition.project.id}?substage=${stageDefinition._id}&claimed=true`}
          >
            {Object.values(SubstageInstanceStatus).reduce(
              (total, status) => total + statusCounts.claimed[status],
              0,
            )}
          </a>
        ),
      },
      {
        claimed: 'Unclaimed',
        ...Object.entries(statusCounts.unclaimed).reduce((acc, [status, count]) => {
          const statusKey = SubstageInstanceStatus[status as keyof typeof SubstageInstanceStatus];

          acc[statusKey] = (
            <a
              key={`${name}/unclaimed/${status}`}
              className="text-info-700"
              href={`${toolSubstagesUrl}/${stageDefinition.project.id}?substage=${stageDefinition._id}&claimed=false&status=${status}`}
            >
              {count}
            </a>
          );
          return acc;
        }, {} as { [key in SubstageInstanceStatusValues]: ReactElement }),
        total: (
          <a
            key={`${name}/claimed/total`}
            className="text-info-700"
            href={`${toolSubstagesUrl}/${stageDefinition.project.id}?substage=${stageDefinition._id}&claimed=false`}
          >
            {Object.values(SubstageInstanceStatus).reduce(
              (total, status) => total + statusCounts.unclaimed[status],
              0,
            )}
          </a>
        ),
      },
      {
        claimed: 'Total',
        ...Object.keys(SubstageInstanceStatus).reduce((counts, key) => {
          const statusKey = SubstageInstanceStatus[key as keyof typeof SubstageInstanceStatus];

          counts[statusKey] = (
            <a
              key={`${name}/unclaimed/${statusKey}`}
              className="text-info-700"
              href={`${toolSubstagesUrl}/${stageDefinition.project.id}?substage=${stageDefinition._id}&status=${statusKey}`}
            >
              {(statusCounts.unclaimed[statusKey] || 0) + (statusCounts.claimed[statusKey] || 0)}
            </a>
          );

          return counts;
        }, {} as { [key in SubstageInstanceStatusValues]: ReactElement }),

        total: (
          <a
            key={`${name}/claimed/total`}
            className="text-info-700"
            href={`${toolSubstagesUrl}/${stageDefinition.project.id}?substage=${stageDefinition._id}`}
          >
            {Object.values(SubstageInstanceStatus).reduce(
              (total, status) =>
                total + statusCounts.unclaimed[status] + statusCounts.claimed[status],
              0,
            )}
          </a>
        ),
      },
    ],
  }));

  return (
    <div className="border border-solid border-neutral-200 px-4 py-4 flex flex-col gap-2">
      <Typography.H3>Substages Breakdown</Typography.H3>
      {tablesData.map(substageData => (
        <div
          key={substageData.id}
          className="border border-solid border-neutral-200 px-4 py-4 flex flex-col gap-2"
        >
          <Typography.H5>{substageData.substage}</Typography.H5>
          <Table columns={TABLE_COLUMNS} data={substageData.data} divideRows manualPagination />
        </div>
      ))}
    </div>
  );
};

export default StageDefinitionBreakdownPanel;
